import { Alert, AlertColor } from "@mui/material"
import { useConventionRegistration } from "contexts/ConventionRegistrationContext"
import useSearchParams from "hooks/useSearchParams"
import { useUser } from "hooks/useUser"
import { api } from "lib/api"
import ConventionSession from "models/convention-api/v1/ConventionSession"
import { SnackbarKey, useSnackbar } from "notistack"
import React, { useEffect, useState } from "react"
import useAsyncEffect from "use-async-effect"

const useScheduleSession = (session: ConventionSession) => {
  const [isScheduled, setIsScheduled] = useState<boolean | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const snackbar = useSnackbar()
  const conventionRegistration = useConventionRegistration()
  const user = useUser()

  const searchParams = useSearchParams()
  const sessionIdToRemind = searchParams.get("sessionIdToRemind")
  const sessionIdToRemoveReminder = searchParams.get("sessionIdToRemoveReminder")

  const sessionRegistration = ConventionSession.useOne(() => {
    if (!user) return null
    if (isScheduled !== null) return null

    return {
      filter: {
        convention_user_id: user.id,
        id: session.id,
      },
    }
  })

  useEffect(() => {
    if (sessionRegistration.data === undefined && sessionRegistration.isValidating) return
    if (isScheduled !== null) return
    setIsScheduled(!!sessionRegistration.data)
  }, [sessionRegistration.data, sessionRegistration.isValidating])

  const showSnackbar = (message: string, severity: AlertColor = "info") => {
    snackbar.enqueueSnackbar(message, {
      autoHideDuration: 6000,
      content: (key: SnackbarKey) => (
        <Alert onClose={() => snackbar.enqueueSnackbar(key)} severity={severity}>
          {message}
        </Alert>
      ),
    })
  }

  const schedule = () => {
    setIsLoading(true)
    searchParams.set("sessionIdToRemind", session.id)
  }

  const undoSchedule = () => {
    setIsLoading(true)
    searchParams.set("sessionIdToRemoveReminder", session.id)
  }

  useAsyncEffect(async () => {
    if (!sessionIdToRemind && !sessionIdToRemoveReminder) return

    if (!conventionRegistration.isRegistered) {
      await conventionRegistration.openRegisterDialog()
      setIsLoading(false)
      return
    }

    try {
      if (sessionIdToRemind === session.id) {
        await api.registerForSession({ req: undefined }, session.id)
        setIsScheduled(true)
        showSnackbar("You’re registered!", "success")
        await searchParams.delete("sessionIdToRemind")
      }

      if (sessionIdToRemoveReminder === session.id) {
        await api.deleteSessionRegistration({ req: undefined }, session.id)
        setIsScheduled(false)
        showSnackbar("You’re not registered!", "success")
        await searchParams.delete("sessionIdToRemoveReminder")
      }
    } catch (error) {
      console.error(error)
      showSnackbar("Something went wrong", "error")
    } finally {
      setIsLoading(false)
    }
  }, [
    sessionIdToRemind,
    sessionIdToRemoveReminder,
    conventionRegistration.isRegistered,
    session.id,
    isLoading,
  ])

  return {
    isScheduled: !!isScheduled,
    schedule,
    undoSchedule,
    isLoading:
      isLoading || (sessionRegistration.data === undefined && sessionRegistration.isValidating),
  }
}

export default useScheduleSession
